import Phaser from 'phaser';
import {
  AXIS_DEADZONE,
  BindingStore,
  GAMEPAD_BUTTON_TO_ACTION,
  STORAGE_KEY,
  type GameAction,
  type PlayerId,
} from '@/systems/InputBindings';

/**
 * InputManager — Phaser polling adapter over the pure BindingStore.
 *
 * Game code asks "is LIGHT down for P1?" and never touches raw key codes or
 * gamepad indices. Binding config + persistence live in InputBindings.ts
 * (framework-free, unit-tested); this class only does live polling.
 */
export class InputManager {
  readonly bindings = new BindingStore();

  private keys: Record<PlayerId, Partial<Record<GameAction, Phaser.Input.Keyboard.Key>>> = {
    1: {},
    2: {},
  };

  /** Load persisted binds from localStorage (guarded for non-DOM envs). */
  loadPersisted(): void {
    if (typeof localStorage === 'undefined') return;
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) this.bindings.load(saved);
  }

  /** Persist current binds to localStorage (guarded). */
  persist(): void {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(STORAGE_KEY, this.bindings.serialize());
  }

  /**
   * Register Phaser Key objects for a scene. Call in Scene.create().
   * Rebuilds the live key map from current bindings.
   */
  attach(scene: Phaser.Scene, player: PlayerId = 1): void {
    const kb = scene.input.keyboard;
    if (!kb) return;
    const binds = this.bindings.get(player);
    const map: Partial<Record<GameAction, Phaser.Input.Keyboard.Key>> = {};
    (Object.keys(binds) as GameAction[]).forEach((action) => {
      map[action] = kb.addKey(binds[action], false);
    });
    this.keys[player] = map;
  }

  /** True while the action's key OR mapped gamepad input is held. */
  isDown(scene: Phaser.Scene, player: PlayerId, action: GameAction): boolean {
    const key = this.keys[player][action];
    if (key?.isDown) return true;
    return this.gamepadActionDown(scene, player, action);
  }

  /** True only on the frame the key went down (edge trigger). */
  justDown(player: PlayerId, action: GameAction): boolean {
    const key = this.keys[player][action];
    return key ? Phaser.Input.Keyboard.JustDown(key) : false;
  }

  /** True only on the frame the key was released (edge trigger). */
  justUp(player: PlayerId, action: GameAction): boolean {
    const key = this.keys[player][action];
    return key ? Phaser.Input.Keyboard.JustUp(key) : false;
  }

  private gamepadActionDown(scene: Phaser.Scene, player: PlayerId, action: GameAction): boolean {
    const pads = scene.input.gamepad?.gamepads ?? [];
    const pad = pads[player - 1];
    if (!pad) return false;

    const entry = Object.entries(GAMEPAD_BUTTON_TO_ACTION).find(([, a]) => a === action);
    if (entry) {
      const buttonIndex = Number(entry[0]);
      if (pad.buttons[buttonIndex]?.pressed) return true;
    }

    const stick = pad.leftStick ?? { x: 0, y: 0 };
    if (action === 'LEFT' && stick.x < -AXIS_DEADZONE) return true;
    if (action === 'RIGHT' && stick.x > AXIS_DEADZONE) return true;
    if (action === 'UP' && stick.y < -AXIS_DEADZONE) return true;
    if (action === 'DOWN' && stick.y > AXIS_DEADZONE) return true;

    return false;
  }
}

// Re-export pure types/consts for convenient single-import consumption.
export {
  ACTIONS,
  BindingStore,
  P1_DEFAULTS,
  P2_DEFAULTS,
  STORAGE_KEY,
  GAMEPAD_BUTTON_TO_ACTION,
  type GameAction,
  type PlayerId,
  type KeyBindings,
} from '@/systems/InputBindings';
